import React from "react";

const PrivacyPolicy = () => (
  <main className="min-h-screen bg-gray-50 py-8">
    <div className="max-w-6xl mx-auto bg-white rounded-xl shadow p-8">
      <h1 className="text-2xl font-bold text-[#0a363c] mb-2 text-center">Privacy Policy</h1>
      <p className="text-sm text-gray-500 mb-8 text-center">
        Last Updated: December 16, 2025
      </p>
      <section className="mb-8">
        <p className="text-[#0a363c]/80">
          This Privacy Policy explains how <strong>EmoyenTECH SAS</strong> (“Company,” “we,” “us,” or “our”) collects, uses, shares, and protects your personal data when you use our website, applications and services through CashCanvas (“Service”).<br />
          We respect your privacy and comply with major data protection laws, including the <strong> General Data Protection Regulation (GDPR), UK GDPR, California Consumer Privacy Act (CCPA/CPRA), Personal Information Protection and Electronic Documents Act (PIPEDA) </strong>, and other applicable laws.
        </p>
      </section>
      <section className="mb-8">
        <h2 className="text-xl font-bold text-[#0a363c] mb-2">1. Who We Are</h2>
        <p className="text-[#0a363c]/80">
          <strong>EmoyenTECH SAS</strong><br />
          Mulhouse, France<br />
          Contact: <a href="mailto:support@mycashcanvas.com" className="text-btn-primary underline"> <strong>support@mycashcanvas.com</strong></a><br />
          We are the data controller for personal information we collect from users.
        </p>
      </section>
      <section className="mb-8">
        <h2 className="text-xl font-bold text-[#0a363c] mb-2">2. What Data We Collect</h2>
        <p className="text-[#0a363c]/80 font-semibold">a. Personal Information You Provide</p>
        <ul className="list-disc pl-6 text-[#0a363c]/80">
          <li>Name (if provided)</li>
          <li>Email address</li>
          <li>Year of Birth</li>
          <li>Billing and payment data (processed by Stripe)</li>
          <li>Support messages or inquiries</li>
        </ul>
        <p className="text-[#0a363c]/80 font-semibold mt-4">b. Financial Data</p>
        <p className="text-[#0a363c]/80">
          Pseudoanonymized Information about your accounts, transactions, budgets, and other financial details you enter when using the Service. This data is the core of our contractual service and is used solely to provide your personalized dashboard and insights.
        </p>
        <p className="text-[#0a363c]/80 font-semibold mt-4">c. Automatically Collected Information</p>
        <ul className="list-disc pl-6 text-[#0a363c]/80">
          <li>Information about how you use the app, such as features accessed, time spent, and device information.</li>
          <li>Cookie data (see Section 8)</li>
        </ul>
        <p className="text-[#0a363c]/80 font-semibold mt-4">d. Data from Third-Party Services</p>
        <ul className="list-disc pl-6 text-[#0a363c]/80">
          <li><strong>Firebase Auth:</strong> Identity and authentication info</li>
          <li><strong>Stripe:</strong> Payment information (we do not store card numbers)</li>
          <li><strong>OpenAI:</strong> Prompts you submit to CashBot</li>
          <li><strong>Google AdSense:</strong> Advertising-related cookies and identifiers</li>
          <li><strong>Financial APIs:</strong> We may collect and display public market data but do not collect your trading information.</li>
        </ul>
      </section>
      <section className="mb-8">
        <h2 className="text-xl font-bold text-[#0a363c] mb-2">3. Why We Collect Your Data</h2>
        <table className="w-full text-[#0a363c]/80 mb-4">
          <thead>
            <tr>
              <th className="text-left font-semibold">Purpose</th>
              <th className="text-left font-semibold">Legal Basis</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>To provide and maintain the Service</td>
              <td>Contractual necessity</td>
            </tr>
            <tr>
              <td>To personalize your dashboard and insights</td>
              <td>Legitimate interests</td>
            </tr>
            <tr>
              <td>To process subscriptions and payments</td>
              <td>Contractual necessity</td>
            </tr>
            <tr>
              <td>To comply with legal obligations</td>
              <td>Legal obligation</td>
            </tr>
            <tr>
              <td>To respond to user inquiries</td>
              <td>Legitimate interests</td>
            </tr>
            <tr>
              <td>To improve the Service</td>
              <td>Legitimate interests</td>
            </tr>
            <tr>
              <td>To serve relevant ads</td>
              <td>Consent</td>
            </tr>
            <tr>
              <td>To protect our systems from abuse</td>
              <td>Legitimate interests</td>
            </tr>
          </tbody>
        </table>
      </section>
      <section className="mb-8">
        <h2 className="text-xl font-bold text-[#0a363c] mb-2">4. How We Use Your Information</h2>
        <ul className="list-disc pl-6 text-[#0a363c]/80">
          <li>Create and manage user accounts</li>
          <li>Provide financial tracking and AI-driven insights</li>
          <li>Process payments and subscriptions (via Stripe, we do not store card numbers)</li>
          <li>Improve our platform’s performance and reliability</li>
          <li>Send important communications (e.g., notices, feature updates)</li>
          <li>Offer personalized ads and content (if you consent)</li>
        </ul>
      </section>
      <section className="mb-8">
        <h2 className="text-xl font-bold text-[#0a363c] mb-2">5. Who We Share It With</h2>
        <p className="text-[#0a363c]/80">
          We do not sell your personal information. We may share your data with:
        </p>
        <table className="w-full text-[#0a363c]/80 mb-4">
          <thead>
            <tr>
              <th className="text-left font-semibold">Recipient</th>
              <th className="text-left font-semibold">Purpose</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Stripe</strong></td>
              <td>Payment processing</td>
            </tr>
            <tr>
              <td><strong>Firebase (Google)</strong></td>
              <td>Authentication and cloud services</td>
            </tr>
            <tr>
              <td><strong>OpenAI</strong></td>
              <td>AI-powered chat responses (you control prompts). Prompts are processed under enterprise terms and are not used to train the OpenAI model.</td>
            </tr>
            <tr>
              <td><strong>Google AdSense</strong></td>
              <td>Ads display and analytics (only if you consent)</td>
            </tr>
            <tr>
              <td><strong>IT & analytics providers</strong></td>
              <td>Infrastructure and usage monitoring (e.g., Cloudflare, Google Analytics)</td>
            </tr>
            <tr>
              <td><strong>Legal or government authorities</strong></td>
              <td>When required by law</td>
            </tr>
          </tbody>
        </table>
        <p className="text-[#0a363c]/80">
          All third parties are contractually required to protect your information under data protection agreements (DPAs or SCCs where applicable).
        </p>
      </section>
      <section className="mb-8">
        <h2 className="text-xl font-bold text-[#0a363c] mb-2">6. How Long We Keep Your Data</h2>
        <ul className="list-disc pl-6 text-[#0a363c]/80">
          <li>As long as you have an account or as needed to provide services, comply with legal obligations, resolve disputes, and enforce our agreements.</li>
          <li>You can request deletion at any time by emailing <a href="mailto:support@mycashcanvas.com" className="text-btn-primary underline">support@mycashcanvas.com</a>.</li>
        </ul>
      </section>
      <section className="mb-8">
        <h2 className="text-xl font-bold text-[#0a363c] mb-2">7. Your Rights</h2>
        <ul className="list-disc pl-6 text-[#0a363c]/80">
          <li>You may access, update, or delete your personal information at any time from your account settings.</li>
          <li>You may request a copy of your data or ask us to delete your account by contacting support.</li>
          <li>You may opt out of marketing communications at any time.</li>
        </ul>
        <p className="text-[#0a363c]/80">
          To exercise your rights, contact us at <a href="mailto:support@mycashcanvas.com" className="text-btn-primary underline"><strong>support@mycashcanvas.com</strong></a>. We may need to verify your identity before acting on your request.
        </p>
      </section>
      <section className="mb-8">
        <h2 className="text-xl font-bold text-[#0a363c] mb-2">8. Specific Rights for California Consumers (CCPA/CPRA)</h2>
        <p className=' text-[#0a363c]/80'>If you are a California resident, you have the following additional rights regarding your personal information:</p>
        <ul className="list-disc pl-6 text-[#0a363c]/80">
          <li><strong>Right to Know:</strong> You can request the categories and specific pieces of personal information we have collected about you.</li>
          <li><strong>Right to Delete:</strong> You have the right to request the deletion of your personal information, subject to certain exceptions.</li>
          <li><strong>Right to Opt-Out of Selling/Sharing:</strong> We do not sell your personal information. We will respect your right to limit the sharing of your data for cross-context behavioral advertising through your cookie preferences.</li>
        </ul>
        <p className="text-[#0a363c]/80">
          To exercise these rights, please contact us at <a href="mailto:support@mycashcanvas.com" className="text-btn-primary underline"><strong>support@mycashcanvas.com</strong></a>.
        </p>
      </section>
      <section className="mb-8">
        <h2 className="text-xl font-bold text-[#0a363c] mb-2">9. Cookies and Tracking Technologies</h2>
        <ul className="list-disc pl-6 text-[#0a363c]/80">
          <li>User session management</li>
          <li>Analytics (Google Analytics)</li>
          <li>Personalized ads (Google AdSense)</li>
          <li>Service improvement</li>
        </ul>
        <p className="text-[#0a363c]/80">
          You may opt out of non-essential cookies via our cookie banner or browser settings.
        </p>
      </section>
      <section className="mb-8">
        <h2 className="text-xl font-bold text-[#0a363c] mb-2">10. International Data Transfers</h2>
        <p className="text-[#0a363c]/80">
          We may transfer your data to countries outside the EU/EEA (e.g., United States) for storage and processing by our service providers. Where we do, we use:
        </p>
        <ul className="list-disc pl-6 text-[#0a363c]/80">
          <li>Standard Contractual Clauses (SCCs)</li>
          <li>Data Protection Agreements</li>
          <li>Certifications under relevant frameworks (e.g., EU-U.S. Data Privacy Framework)</li>
        </ul>
      </section>
      <section className="mb-8">
        <h2 className="text-xl font-bold text-[#0a363c] mb-2">11. Data Security</h2>
        <ul className="list-disc pl-6 text-[#0a363c]/80">
          <li>HTTPS encryption</li>
          <li>Secure cloud storage (e.g., Firebase, AWS)</li>
          <li>Role-based access controls</li>
          <li>Monitoring for unauthorized access</li>
        </ul>
        <p className="text-[#0a363c]/80">
          However, no system is 100% secure. Use strong passwords and secure your device when accessing the Service.
        </p>
      </section>
      <section className="mb-8">
        <h2 className="text-xl font-bold text-[#0a363c] mb-2">12. Children's Privacy</h2>
        <p className="text-[#0a363c]/80">
          CashCanvas is not intended for users under <strong>13 years of age</strong>. Users aged <strong>13–17</strong> must have parental or guardian supervision. We do not knowingly collect personal data from children without appropriate consent. If you believe we have such information, please contact us to have it removed.
        </p>
      </section>
      <section className="mb-8">
        <h2 className="text-xl font-bold text-[#0a363c] mb-2">13. Changes to This Policy</h2>
        <ul className="list-disc pl-6 text-[#0a363c]/80">
          <li>Update the “Last Updated” date above</li>
          <li>Notify you through the Service or by email (if significant)</li>
        </ul>
        <p className="text-[#0a363c]/80">
          Continued use of the Service means you accept the revised policy.
        </p>
      </section>
      <section className="mb-8">
        <h2 className="text-xl font-bold text-[#0a363c] mb-2">14. Contact Us</h2>
        <p className="text-[#0a363c]/80">
          If you have any questions or concerns about this Privacy Policy or your personal data, contact:<br />
          <strong>EmoyenTECH SAS</strong><br />
          <strong>68 rue de la couronne</strong><br />
          <strong>68400 Riedishiem</strong><br />
          <br/>
          Email: <a href="mailto:support@mycashcanvas.com" className="text-btn-primary underline"><strong>support@mycashcanvas.com</strong></a><br />
          Subject: Privacy Policy Inquiry<br />
          If you are in the EU or UK and have concerns, you may contact your local data protection authority, such as <strong>CNIL</strong> in France.
        </p>
      </section>
    </div>
  </main>
);

export default PrivacyPolicy;